import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import LocationSelect from '../components/LocationSelect';
import { splitHeadquarters, joinHeadquarters } from '../lib/locations';
import { useProfile } from '../hooks/useProfile';

interface Firm {
  id: string;
  name: string;
  description: string;
  headquarters: string;
  type: string;
  aum_min: number;
  aum_max: number;
  website: string;
  inception_date: string;
  owner_id: string;
}

interface Fund {
  id: string;
  name: string;
  asset_class: string;
  strategy: string;
  aum_min: number;
  aum_max: number;
  minimum_subscription: number;
  management_fee: number;
  performance_fee: number;
  inception_date: string;
  status: string;
  regional_focus: string;
  vehicle_type: string;
}

interface TeamMember {
  id: string;
  name: string;
  title: string;
  bio: string;
  photo_url: string;
  display_order: number;
}

const classColors: Record<string, { color: string; bg: string }> = {
  hedge_fund: { color: '#0D6E8A', bg: '#EFF8FB' },
  pe: { color: '#92640A', bg: '#FDF6E7' },
  vc: { color: '#1D4ED8', bg: '#EFF4FF' },
  re: { color: '#B91C1C', bg: '#FEF2F2' },
  credit: { color: '#0F766E', bg: '#F0FDFA' },
  infrastructure: { color: '#6D28D9', bg: '#F5F3FF' },
};

const classLabels: Record<string, string> = {
  hedge_fund: 'Hedge Fund', pe: 'Private Equity', vc: 'Venture Capital',
  re: 'Real Estate', credit: 'Credit', infrastructure: 'Infrastructure',
};

const firmTypes = [
  'Hedge Fund', 'Private Equity', 'Venture Capital', 'Family Office',
  'Endowment', 'Pension Fund', 'Fund of Funds', 'Asset Manager', 'Other'
];

function toTitleCase(str: string): string {
  if (!str) return '-';
  return str.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

function maskAUM(min: number, max: number): string {
  if (!min) return 'Undisclosed';
  const avg = (min + max) / 2;
  if (avg < 100) return 'Under $100mm';
  if (avg < 500) return '$100mm – $500mm';
  if (avg < 1000) return '$500mm – $1bn';
  if (avg < 5000) return '$1bn – $5bn';
  return '$5bn+';
}

export default function FirmProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { firm: myFirm, profile } = useProfile();
  const [firm, setFirm] = useState<Firm | null>(null);
  const [funds, setFunds] = useState<Fund[]>([]);
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'none' | 'pending' | 'approved'>('none');
  const [requesting, setRequesting] = useState(false);
  const [selectedFund, setSelectedFund] = useState<Fund | null>(null);

  // Edit firm state
  const [editingFirm, setEditingFirm] = useState(false);
  const [firmForm, setFirmForm] = useState<Partial<Firm>>({});
  const [savingFirm, setSavingFirm] = useState(false);

  // Add team member state
  const [showAddTeam, setShowAddTeam] = useState(false);
  const [teamForm, setTeamForm] = useState({ name: '', title: '', bio: '' });
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [savingTeam, setSavingTeam] = useState(false);
  const [teamError, setTeamError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const isManager = profile?.role === 'manager';
  const isOwnFirm = myFirm?.id === id;

  useEffect(() => { fetchAll(); }, [id]);
  useEffect(() => { if (myFirm && id && !isOwnFirm) checkConnection(); }, [myFirm, id]);

  const fetchAll = async () => {
    const { data: firmData } = await supabase.from('firms').select('*').eq('id', id).single();
    if (firmData) { setFirm(firmData); setFirmForm(firmData); }

    const { data: fundsData } = await supabase.from('funds').select('*').eq('firm_id', id).eq('status', 'open').order('created_at', { ascending: false });
    if (fundsData) { setFunds(fundsData); if (fundsData.length > 0) setSelectedFund(fundsData[0]); }

    const { data: teamData } = await supabase.from('team_members').select('*').eq('firm_id', id).order('display_order', { ascending: true });
    if (teamData) setTeam(teamData);

    setLoading(false);
  };

  const checkConnection = async () => {
    if (!myFirm) return;
    const { data } = await supabase.from('connections').select('status').eq('allocator_firm_id', myFirm.id).eq('manager_firm_id', id).maybeSingle();
    if (data) {
      setConnectionStatus(data.status === 'approved' ? 'approved' : 'pending');
      setIsConnected(data.status === 'approved');
    }
  };

  const handleConnect = async () => {
    if (!myFirm || !firm || !profile) return;
    setRequesting(true);
    const user = (await supabase.auth.getUser()).data.user;
    if (!user) { setRequesting(false); return; }

    await supabase.from('connections').insert({ allocator_firm_id: myFirm.id, manager_firm_id: firm.id, fund_id: null, requested_by: user.id, status: 'pending' });
    setConnectionStatus('pending');

    try {
      const { data: ownerProfile } = await supabase.from('profiles').select('full_name, email').eq('id', firm.owner_id).single();
      if (ownerProfile) {
        await supabase.functions.invoke('send-email-notification', {
          body: { type: 'fund_access_request', data: { manager_email: ownerProfile.email, manager_name: ownerProfile.full_name || 'there', allocator_firm_name: myFirm.name, allocator_name: profile.full_name || 'An allocator', fund_name: `all funds at ${firm.name}` } }
        });
      }
    } catch (err) { console.error('Email failed:', err); }
    setRequesting(false);
  };

  const handleSaveFirm = async () => {
    if (!firm) return;
    setSavingFirm(true);
    const { error } = await supabase.from('firms').update({
      name: firmForm.name,
      description: firmForm.description,
      headquarters: firmForm.headquarters,
      type: firmForm.type,
      aum_min: firmForm.aum_min,
      aum_max: firmForm.aum_max,
      website: firmForm.website,
      inception_date: firmForm.inception_date,
    }).eq('id', firm.id);
    if (!error) { setFirm({ ...firm, ...firmForm } as Firm); setEditingFirm(false); }
    setSavingFirm(false);
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleAddTeam = async () => {
    if (!myFirm || !teamForm.name.trim()) { setTeamError('Name is required.'); return; }
    setSavingTeam(true);
    setTeamError('');

    let photoUrl = '';
    if (photoFile) {
      const ext = photoFile.name.split('.').pop();
      const path = `${myFirm.id}/${Date.now()}.${ext}`;
      const { error: uploadError } = await supabase.storage.from('team-photos').upload(path, photoFile);
      if (uploadError) { setTeamError('Photo upload failed: ' + uploadError.message); setSavingTeam(false); return; }
      const { data: urlData } = supabase.storage.from('team-photos').getPublicUrl(path);
      photoUrl = urlData.publicUrl;
    }

    const { error } = await supabase.from('team_members').insert({ firm_id: myFirm.id, name: teamForm.name.trim(), title: teamForm.title.trim(), bio: teamForm.bio.trim(), photo_url: photoUrl, display_order: team.length });
    if (error) { setTeamError(error.message); setSavingTeam(false); return; }

    setTeamForm({ name: '', title: '', bio: '' });
    setPhotoFile(null);
    setPhotoPreview(null);
    setShowAddTeam(false);
    fetchAll();
    setSavingTeam(false);
  };

  const handleDeleteTeam = async (memberId: string) => {
    await supabase.from('team_members').delete().eq('id', memberId);
    fetchAll();
  };

  if (loading) return <div style={{ padding: '28px', background: '#F5F6F8', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><div style={{ color: '#6B7280' }}>Loading...</div></div>;
  if (!firm) return <div style={{ padding: '28px', background: '#F5F6F8', minHeight: '100vh' }}><div style={{ color: '#C0222A' }}>Firm not found.</div></div>;

  const cardStyle = { background: '#FFFFFF', border: '1px solid #D0D5DD', borderRadius: '4px', padding: '20px', marginBottom: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' };
  const labelStyle = { fontSize: '11px', color: '#6B7280', marginBottom: '3px' };
  const valueStyle = { fontSize: '13.5px', fontWeight: 500, color: '#0A0A0A' } as const;
  const inputStyle = { width: '100%', background: '#FFFFFF', border: '1px solid #D0D5DD', borderRadius: '3px', color: '#0A0A0A', fontSize: '13px', padding: '9px 12px', outline: 'none', fontFamily: 'system-ui, -apple-system, sans-serif', boxSizing: 'border-box' as const };

  return (
    <div style={{ background: '#F5F6F8', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ background: '#FFFFFF', borderBottom: '1px solid #D0D5DD', padding: '20px 28px' }}>
        <button onClick={() => navigate(-1)} style={{ background: '#FFFFFF', border: '1px solid #D0D5DD', borderRadius: '3px', color: '#6B7280', padding: '6px 12px', cursor: 'pointer', fontSize: '12px', marginBottom: '16px', fontFamily: 'system-ui, -apple-system, sans-serif' }}>← Back</button>

        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ width: '52px', height: '52px', borderRadius: '4px', background: '#FDF6E7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', fontWeight: 700, color: '#C9A84C' }}>
              {firm.name.charAt(0)}
            </div>
            <div>
              <h1 style={{ fontFamily: 'Georgia, serif', fontWeight: 400, fontSize: '26px', color: '#0A0A0A', marginBottom: '4px' }}>{firm.name}</h1>
              <div style={{ fontSize: '13px', color: '#6B7280' }}>{firm.headquarters || '-'} · {toTitleCase(firm.type)}</div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            {isOwnFirm && isManager && (
              <button
                onClick={() => setEditingFirm(true)}
                style={{ padding: '8px 16px', borderRadius: '3px', fontSize: '12px', fontWeight: 500, cursor: 'pointer', fontFamily: 'system-ui, -apple-system, sans-serif', border: '1px solid #D0D5DD', background: '#FFFFFF', color: '#3D4552' }}
              >Edit firm</button>
            )}

            {!isManager && !isOwnFirm && (
              <div>
                {connectionStatus === 'none' && (
                  <button onClick={handleConnect} disabled={requesting} style={{ padding: '9px 20px', borderRadius: '3px', fontSize: '13px', fontWeight: 500, cursor: requesting ? 'not-allowed' : 'pointer', border: '1px solid #C9A84C', background: '#FDF6E7', color: '#C9A84C', fontFamily: 'system-ui, -apple-system, sans-serif', opacity: requesting ? 0.7 : 1 }}>
                    {requesting ? 'Requesting...' : 'Request Access'}
                  </button>
                )}
                {connectionStatus === 'pending' && (
                  <div style={{ padding: '9px 20px', borderRadius: '3px', fontSize: '13px', fontWeight: 500, border: '1px solid #92640A', background: '#FDF6E7', color: '#92640A' }}>⏳ Request sent</div>
                )}
                {connectionStatus === 'approved' && (
                  <div style={{ padding: '9px 20px', borderRadius: '3px', fontSize: '13px', fontWeight: 500, border: '1px solid #C9A84C', background: '#FDF6E7', color: '#C9A84C' }}>✓ Connected</div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={{ padding: '24px 28px' }}>
        {/* Tabs */}
        <div style={{ display: 'flex', gap: '2px', background: '#FFFFFF', border: '1px solid #D0D5DD', borderRadius: '4px', padding: '4px', width: 'fit-content', marginBottom: '24px' }}>
          {(isOwnFirm && isManager ? ['overview', 'funds', 'team'] : ['overview', 'funds']).map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)} style={{ padding: '6px 16px', borderRadius: '3px', fontSize: '13px', cursor: 'pointer', border: 'none', background: activeTab === tab ? '#F5F6F8' : 'transparent', color: activeTab === tab ? '#0A0A0A' : '#6B7280', fontFamily: 'system-ui, -apple-system, sans-serif', fontWeight: activeTab === tab ? 500 : 400, textTransform: 'capitalize' }}>{tab}</button>
          ))}
        </div>

        {activeTab === 'overview' && (
          <>
            <div style={cardStyle}>
              <div style={{ fontSize: '13px', fontWeight: 500, color: '#0A0A0A', marginBottom: '16px' }}>Firm overview</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '16px' }}>
                {[
                  { label: 'Type', value: toTitleCase(firm.type) },
                  { label: 'Headquarters', value: firm.headquarters || '-' },
                  { label: 'Firm AUM', value: isConnected || isOwnFirm ? (firm.aum_min ? `$${firm.aum_min}–${firm.aum_max}mm` : '-') : maskAUM(firm.aum_min, firm.aum_max) },
                  { label: 'Inception', value: firm.inception_date ? new Date(firm.inception_date).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }) : '-' },
                ].map((item, i) => (
                  <div key={i}><div style={labelStyle}>{item.label}</div><div style={valueStyle}>{item.value}</div></div>
                ))}
              </div>
              {firm.website && <div><div style={labelStyle}>Website</div><a href={firm.website} target="_blank" rel="noreferrer" style={{ fontSize: '13.5px', color: '#C9A84C', textDecoration: 'none' }}>{firm.website}</a></div>}
            </div>

            {firm.description && (
              <div style={cardStyle}>
                <div style={{ fontSize: '13px', fontWeight: 500, color: '#0A0A0A', marginBottom: '12px' }}>About</div>
                <p style={{ fontSize: '13.5px', color: '#3D4552', lineHeight: 1.7 }}>{firm.description}</p>
              </div>
            )}

            {team.length > 0 && (
              <div style={cardStyle}>
                <div style={{ fontSize: '13px', fontWeight: 500, color: '#0A0A0A', marginBottom: '16px' }}>Team</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '16px' }}>
                  {team.map(member => (
                    <div key={member.id} style={{ background: '#F5F6F8', borderRadius: '4px', padding: '16px', border: '1px solid #D0D5DD' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                        {member.photo_url ? (
                          <img src={member.photo_url} alt={member.name} style={{ width: '44px', height: '44px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                        ) : (
                          <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: '#FDF6E7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', fontWeight: 600, color: '#C9A84C', flexShrink: 0 }}>{member.name.charAt(0)}</div>
                        )}
                        <div>
                          <div style={{ fontSize: '13px', fontWeight: 600, color: '#0A0A0A' }}>{member.name}</div>
                          {member.title && <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '2px' }}>{member.title}</div>}
                        </div>
                      </div>
                      {member.bio && <p style={{ fontSize: '12px', color: '#6B7280', lineHeight: 1.6, margin: 0 }}>{member.bio}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === 'funds' && (
          <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: '20px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {funds.map(fund => {
                const cls = classColors[fund.asset_class] || { color: '#6B7280', bg: '#F5F6F8' };
                return (
                  <div key={fund.id} onClick={() => setSelectedFund(fund)} style={{ background: selectedFund?.id === fund.id ? '#F5F6F8' : '#FFFFFF', border: `1px solid ${selectedFund?.id === fund.id ? '#C9A84C' : '#D0D5DD'}`, borderRadius: '4px', padding: '12px 14px', cursor: 'pointer' }}>
                    <div style={{ fontSize: '12px', fontWeight: 500, color: '#0A0A0A', marginBottom: '4px' }}>{fund.name}</div>
                    <span style={{ fontSize: '9px', fontWeight: 500, padding: '2px 6px', borderRadius: '20px', background: cls.bg, color: cls.color, letterSpacing: '0.5px', textTransform: 'uppercase' }}>{classLabels[fund.asset_class] || fund.asset_class}</span>
                  </div>
                );
              })}
            </div>

            {selectedFund && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <div style={{ fontFamily: 'Georgia, serif', fontWeight: 400, fontSize: '20px', color: '#0A0A0A' }}>{selectedFund.name}</div>
                  <button onClick={() => navigate(`/alt-connect/fund/${selectedFund.id}`)} style={{ background: '#0A1628', border: 'none', borderRadius: '3px', color: '#FFFFFF', fontSize: '12px', padding: '7px 14px', cursor: 'pointer', fontFamily: 'system-ui, -apple-system, sans-serif' }}>Full profile →</button>
                </div>

                <div style={cardStyle}>
                  <div style={{ fontSize: '13px', fontWeight: 500, color: '#0A0A0A', marginBottom: '16px' }}>Overview</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '16px' }}>
                    {[
                      { label: 'Strategy', value: selectedFund.strategy || '-' },
                      { label: 'AUM', value: (isConnected || isOwnFirm) ? (selectedFund.aum_min ? `$${selectedFund.aum_min}–${selectedFund.aum_max}mm` : '-') : maskAUM(selectedFund.aum_min, selectedFund.aum_max) },
                      { label: 'Inception', value: selectedFund.inception_date ? new Date(selectedFund.inception_date).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }) : '-' },
                    ].map((item, i) => (
                      <div key={i}><div style={labelStyle}>{item.label}</div><div style={valueStyle}>{item.value}</div></div>
                    ))}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                    {[
                      { label: 'Regional focus', value: selectedFund.regional_focus || '-' },
                      { label: 'Vehicle type', value: selectedFund.vehicle_type || '-' },
                      { label: 'Min subscription', value: selectedFund.minimum_subscription ? `$${selectedFund.minimum_subscription}mm` : '-' },
                    ].map((item, i) => (
                      <div key={i}><div style={labelStyle}>{item.label}</div><div style={valueStyle}>{item.value}</div></div>
                    ))}
                  </div>
                </div>

                <div style={cardStyle}>
                  <div style={{ fontSize: '13px', fontWeight: 500, color: '#0A0A0A', marginBottom: '16px' }}>Terms</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                    {[
                      { label: 'Management fee', value: selectedFund.management_fee ? `${selectedFund.management_fee}%` : '-' },
                      { label: 'Performance fee', value: selectedFund.performance_fee ? `${selectedFund.performance_fee}%` : '-' },
                      { label: 'Min subscription', value: selectedFund.minimum_subscription ? `$${selectedFund.minimum_subscription}mm` : '-' },
                    ].map((item, i) => (
                      <div key={i}><div style={labelStyle}>{item.label}</div><div style={valueStyle}>{item.value}</div></div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'team' && isOwnFirm && isManager && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
              <button onClick={() => setShowAddTeam(true)} style={{ background: '#0A1628', border: 'none', borderRadius: '3px', color: '#FFFFFF', fontSize: '13px', fontWeight: 600, padding: '9px 18px', cursor: 'pointer', fontFamily: 'system-ui, -apple-system, sans-serif' }}>+ Add member</button>
            </div>

            {team.length === 0 ? (
              <div style={{ ...cardStyle, textAlign: 'center', padding: '60px' }}>
                <div style={{ fontSize: '16px', color: '#0A0A0A', fontFamily: 'Georgia, serif', fontWeight: 400, marginBottom: '8px' }}>No team members yet</div>
                <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '24px' }}>Add your team to show investors who they'll be working with</div>
                <button onClick={() => setShowAddTeam(true)} style={{ background: '#0A1628', border: 'none', borderRadius: '3px', color: '#FFFFFF', fontSize: '13px', fontWeight: 600, padding: '10px 24px', cursor: 'pointer', fontFamily: 'system-ui, -apple-system, sans-serif' }}>Add first member</button>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
                {team.map(member => (
                  <div key={member.id} style={{ ...cardStyle, marginBottom: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                      {member.photo_url ? (
                        <img src={member.photo_url} alt={member.name} style={{ width: '48px', height: '48px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                      ) : (
                        <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: '#FDF6E7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', fontWeight: 600, color: '#C9A84C', flexShrink: 0 }}>{member.name.charAt(0)}</div>
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: '#0A0A0A' }}>{member.name}</div>
                        {member.title && <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>{member.title}</div>}
                      </div>
                      <button onClick={() => handleDeleteTeam(member.id)} style={{ background: '#FFFFFF', border: '1px solid #C0222A', borderRadius: '3px', color: '#C0222A', fontSize: '11px', padding: '4px 8px', cursor: 'pointer', fontFamily: 'system-ui, -apple-system, sans-serif', flexShrink: 0 }}>Remove</button>
                    </div>
                    {member.bio && <p style={{ fontSize: '12px', color: '#6B7280', lineHeight: 1.6, margin: 0 }}>{member.bio}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Edit firm modal */}
      {editingFirm && (
        <div onClick={() => setEditingFirm(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#FFFFFF', border: '1px solid #D0D5DD', borderRadius: '4px', padding: '28px', width: '520px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ fontFamily: 'Georgia, serif', fontWeight: 400, fontSize: '20px', color: '#0A0A0A', marginBottom: '20px' }}>Edit firm details</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={{ fontSize: '11px', color: '#6B7280', display: 'block', marginBottom: '5px' }}>Firm name</label>
                <input style={inputStyle} value={firmForm.name || ''} onChange={e => setFirmForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div>
                <label style={{ fontSize: '11px', color: '#6B7280', display: 'block', marginBottom: '5px' }}>Type</label>
                <select style={inputStyle} value={firmForm.type || ''} onChange={e => setFirmForm(f => ({ ...f, type: e.target.value }))}>
                  {firmTypes.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              {/* firms.headquarters is a single "City, Country" column, so the
                  pair is split for editing and rejoined on change. */}
              <LocationSelect
                country={splitHeadquarters(firmForm.headquarters).country}
                city={splitHeadquarters(firmForm.headquarters).city}
                onCountryChange={v => setFirmForm(f => ({
                  ...f, headquarters: joinHeadquarters(v, splitHeadquarters(f.headquarters).city),
                }))}
                onCityChange={v => setFirmForm(f => ({
                  ...f, headquarters: joinHeadquarters(splitHeadquarters(f.headquarters).country, v),
                }))}
                countryLabel="Headquarters Country"
                cityLabel="Headquarters City"
                labelStyle={{ fontSize: '11px', color: '#6B7280', display: 'block', marginBottom: '5px', fontWeight: 400, letterSpacing: 'normal', textTransform: 'none', fontFamily: 'system-ui' }}
                inputStyle={inputStyle}
              />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ fontSize: '11px', color: '#6B7280', display: 'block', marginBottom: '5px' }}>AUM min ($mm)</label>
                  <input style={inputStyle} type="number" value={firmForm.aum_min || ''} onChange={e => setFirmForm(f => ({ ...f, aum_min: parseFloat(e.target.value) }))} />
                </div>
                <div>
                  <label style={{ fontSize: '11px', color: '#6B7280', display: 'block', marginBottom: '5px' }}>AUM max ($mm)</label>
                  <input style={inputStyle} type="number" value={firmForm.aum_max || ''} onChange={e => setFirmForm(f => ({ ...f, aum_max: parseFloat(e.target.value) }))} />
                </div>
              </div>
              <div>
                <label style={{ fontSize: '11px', color: '#6B7280', display: 'block', marginBottom: '5px' }}>Website</label>
                <input style={inputStyle} value={firmForm.website || ''} onChange={e => setFirmForm(f => ({ ...f, website: e.target.value }))} placeholder="https://" />
              </div>
              <div>
                <label style={{ fontSize: '11px', color: '#6B7280', display: 'block', marginBottom: '5px' }}>Inception date</label>
                <input style={inputStyle} type="date" value={firmForm.inception_date?.split('T')[0] || ''} onChange={e => setFirmForm(f => ({ ...f, inception_date: e.target.value }))} />
              </div>
              <div>
                <label style={{ fontSize: '11px', color: '#6B7280', display: 'block', marginBottom: '5px' }}>Description</label>
                <textarea style={{ ...inputStyle, resize: 'vertical' } as any} rows={4} value={firmForm.description || ''} onChange={e => setFirmForm(f => ({ ...f, description: e.target.value }))} placeholder="Describe your firm..." />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: '20px' }}>
              <button onClick={() => setEditingFirm(false)} style={{ background: '#FFFFFF', border: '1px solid #D0D5DD', borderRadius: '3px', color: '#3D4552', fontSize: '13px', padding: '10px', cursor: 'pointer', fontFamily: 'system-ui, -apple-system, sans-serif' }}>Cancel</button>
              <button onClick={handleSaveFirm} disabled={savingFirm} style={{ background: '#0A1628', border: 'none', borderRadius: '3px', color: '#FFFFFF', fontSize: '13px', fontWeight: 600, padding: '10px', cursor: savingFirm ? 'not-allowed' : 'pointer', fontFamily: 'system-ui, -apple-system, sans-serif', opacity: savingFirm ? 0.7 : 1 }}>{savingFirm ? 'Saving...' : 'Save changes'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Add team member modal */}
      {showAddTeam && (
        <div onClick={() => setShowAddTeam(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#FFFFFF', border: '1px solid #D0D5DD', borderRadius: '4px', padding: '28px', width: '460px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ fontFamily: 'Georgia, serif', fontWeight: 400, fontSize: '20px', color: '#0A0A0A', marginBottom: '20px' }}>Add team member</h3>
            {teamError && <div style={{ background: '#FEF2F2', border: '1px solid #C0222A', borderRadius: '4px', padding: '10px 12px', marginBottom: '16px', fontSize: '12px', color: '#C0222A' }}>{teamError}</div>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={{ fontSize: '11px', color: '#6B7280', display: 'block', marginBottom: '8px' }}>Photo (optional)</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {photoPreview ? (
                    <img src={photoPreview} alt="preview" style={{ width: '52px', height: '52px', borderRadius: '50%', objectFit: 'cover' }} />
                  ) : (
                    <div style={{ width: '52px', height: '52px', borderRadius: '50%', background: '#F5F6F8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', color: '#6B7280' }}>👤</div>
                  )}
                  <button onClick={() => fileRef.current?.click()} style={{ background: '#FFFFFF', border: '1px solid #D0D5DD', borderRadius: '3px', color: '#3D4552', fontSize: '12px', padding: '7px 14px', cursor: 'pointer', fontFamily: 'system-ui, -apple-system, sans-serif' }}>Upload photo</button>
                  <input ref={fileRef} type="file" accept="image/*" onChange={handlePhotoChange} style={{ display: 'none' }} />
                </div>
              </div>
              <div>
                <label style={{ fontSize: '11px', color: '#6B7280', display: 'block', marginBottom: '5px' }}>Full name *</label>
                <input style={inputStyle} value={teamForm.name} onChange={e => setTeamForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. John Smith" />
              </div>
              <div>
                <label style={{ fontSize: '11px', color: '#6B7280', display: 'block', marginBottom: '5px' }}>Title / Role</label>
                <input style={inputStyle} value={teamForm.title} onChange={e => setTeamForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. Chief Investment Officer" />
              </div>
              <div>
                <label style={{ fontSize: '11px', color: '#6B7280', display: 'block', marginBottom: '5px' }}>Bio</label>
                <textarea style={{ ...inputStyle, resize: 'vertical' } as any} rows={4} value={teamForm.bio} onChange={e => setTeamForm(f => ({ ...f, bio: e.target.value }))} placeholder="Brief background and experience..." />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: '20px' }}>
              <button onClick={() => { setShowAddTeam(false); setTeamError(''); }} style={{ background: '#FFFFFF', border: '1px solid #D0D5DD', borderRadius: '3px', color: '#3D4552', fontSize: '13px', padding: '10px', cursor: 'pointer', fontFamily: 'system-ui, -apple-system, sans-serif' }}>Cancel</button>
              <button onClick={handleAddTeam} disabled={savingTeam} style={{ background: '#0A1628', border: 'none', borderRadius: '3px', color: '#FFFFFF', fontSize: '13px', fontWeight: 600, padding: '10px', cursor: savingTeam ? 'not-allowed' : 'pointer', fontFamily: 'system-ui, -apple-system, sans-serif', opacity: savingTeam ? 0.7 : 1 }}>{savingTeam ? 'Saving...' : 'Add member'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
